<script setup>
import { onMounted, onBeforeUnmount, reactive, ref, watch } from 'vue';
import EditorToolbar from './EditorToolbar.vue';

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Start typing...',
  },
  showToolbar: {
    type: Boolean,
    default: true,
  },
  showUndo: {
    type: Boolean,
    default: true,
  },
  showRedo: {
    type: Boolean,
    default: true,
  },
  showSubscript: {
    type: Boolean,
    default: false,
  },
  showSuperscript: {
    type: Boolean,
    default: false,
  },
  showH1: {
    type: Boolean,
    default: false,
  },
  showH2: {
    type: Boolean,
    default: false,
  },
  showBlockquote: {
    type: Boolean,
    default: false,
  },
  editorClass: {
    type: String,
    default: 'view-tray-modal__editor',
  },
  toolbarClass: {
    type: String,
    default: 'view-tray-modal__editor-toolbar',
  },
  buttonClass: {
    type: String,
    default: 'view-tray-modal__toolbar-btn',
  },
  ariaLabelledby: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  ariaDescribedby: {
    type: String,
    default: '',
  },
  ariaInvalid: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const editorRef = ref(null);

const formatState = reactive({
  bold: false,
  italic: false,
  subscript: false,
  superscript: false,
});

const updateFormatState = () => {
  const el = editorRef.value;
  if (!el) return;

  const active = document.activeElement;
  if (active !== el && !el.contains(active)) return;

  try {
    formatState.bold = document.queryCommandState('bold');
    formatState.italic = document.queryCommandState('italic');
    formatState.subscript = document.queryCommandState('subscript');
    formatState.superscript = document.queryCommandState('superscript');
  } catch {
    // queryCommandState can throw in some environments; leave state as-is.
  }
};

const handleCommand = command => {
  editorRef.value?.focus();
  document.execCommand(command, false, null);
  editorRef.value?.focus();
  updateFormatState();
};

const isValidUrl = url => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:', 'mailto:'].includes(parsed.protocol);
  } catch {
    return false;
  }
};

const handleLink = () => {
  editorRef.value?.focus();
  const url = prompt('Enter a URL');
  if (url) {
    if (!isValidUrl(url)) {
      alert('Please enter a valid URL starting with http://, https://, or mailto:');
      return;
    }
    document.execCommand('createLink', false, url);
  }
};

const handleFormatBlock = tag => {
  editorRef.value?.focus();
  document.execCommand('formatBlock', false, `<${tag}>`);
  editorRef.value?.focus();
};

const handleInput = () => {
  const content = editorRef.value?.innerHTML || '';
  emit('update:modelValue', content);
};

const handlePaste = event => {
  event.preventDefault();
  const text = (event.clipboardData || window.clipboardData).getData('text/plain');
  document.execCommand('insertText', false, text);

  handleInput();
};

const clear = () => {
  if (editorRef.value) {
    editorRef.value.innerHTML = '';
    emit('update:modelValue', '');
  }
};

const focus = () => {
  editorRef.value?.focus();
};

const getContent = () => {
  return editorRef.value?.innerHTML || '';
};

onMounted(() => {
  if (props.modelValue && editorRef.value) {
    editorRef.value.innerHTML = props.modelValue;
  }
  document.addEventListener('selectionchange', updateFormatState);
});

onBeforeUnmount(() => {
  document.removeEventListener('selectionchange', updateFormatState);
});

watch(
  () => props.modelValue,
  newValue => {
    if (editorRef.value && editorRef.value.innerHTML !== newValue) {
      editorRef.value.innerHTML = newValue || '';
    }
  }
);

defineExpose({
  clear,
  focus,
  getContent,
  editorRef,
});
</script>

<template>
  <div>
    <EditorToolbar
      v-if="showToolbar"
      :show-undo="showUndo"
      :show-redo="showRedo"
      :show-subscript="showSubscript"
      :show-superscript="showSuperscript"
      :show-h1="showH1"
      :show-h2="showH2"
      :show-blockquote="showBlockquote"
      :toolbar-class="toolbarClass"
      :button-class="buttonClass"
      :bold-active="formatState.bold"
      :italic-active="formatState.italic"
      :subscript-active="formatState.subscript"
      :superscript-active="formatState.superscript"
      @command="handleCommand"
      @link="handleLink"
      @format-block="handleFormatBlock"
    />
    <div
      ref="editorRef"
      :class="editorClass"
      contenteditable="true"
      :placeholder="placeholder"
      role="textbox"
      aria-multiline="true"
      :aria-labelledby="ariaLabelledby || undefined"
      :aria-label="ariaLabelledby ? undefined : placeholder"
      :aria-required="required ? 'true' : undefined"
      :aria-describedby="ariaDescribedby || undefined"
      :aria-invalid="ariaInvalid ? 'true' : undefined"
      @input="handleInput"
      @paste="handlePaste"
    />
  </div>
</template>
